import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Phone, ArrowRight, Home, Settings, Volume2, Waves, Sliders, MessageSquare,
  Award, Users, Shield, Heart, Star, ChevronRight
} from 'lucide-react';
import { services, stats, whyChooseUs, testimonials, businessInfo, heroSlides } from '../mock';
import { BrandsCarousel } from '../components/BrandsShowcase';
import { HeroCarousel } from '../components/HeroCarousel';

const iconMap = {
  Home, Settings, Volume2, Waves, Sliders, MessageSquare, Award, Users, Shield, Heart
};

export const HomePage = () => {
  return (
    <div className="min-h-screen bg-white">
      <HeroCarousel slides={heroSlides}>
        <div className="max-w-4xl">
          <Badge className="mb-6 bg-green-600/20 text-green-700 border-green-600/50 hover:bg-green-600/30 text-sm">
            Premium Home Theater Solutions Since {businessInfo.established}
          </Badge>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            Elevate Your
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-green-600 mt-2">
              Home Cinema Experience
            </span>
          </h1>
          <p className="text-xl text-gray-200 mb-8 leading-relaxed max-w-2xl">
            Expert home theater design, installation, and calibration services. 
            Transform your space into a cinematic masterpiece with Dolby Atmos, 4K projection, 
            and premium audio-visual solutions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mb-12">
            <Link to="/contact">
              <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white text-base px-8 py-6 shadow-lg shadow-green-600/30 hover:shadow-green-600/50 transition-all duration-300 transform hover:scale-105">
                Get Free Consultation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/portfolio">
              <Button size="lg" variant="outline" className="border-2 border-green-600 text-green-700 hover:bg-green-600/10 text-base px-8 py-6 transition-all duration-300">
                View Our Projects
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 pt-8 border-t border-green-200">
            {stats.map((stat, index) => (
              <div key={index} className="text-center lg:text-left">
                <div className="text-4xl sm:text-5xl font-bold text-green-600 mb-1">{stat.value}</div>
                <div className="text-sm text-gray-200">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </HeroCarousel>

      {/* About Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="mb-4 bg-green-600/20 text-green-700 border-green-600/50">
                About MJ Audio
              </Badge>
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                South India's Premier
                <span className="block text-green-600 mt-2">Home Theater Specialist</span>
              </h2>
              <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                With over {stats[0].value} of experience, MJ Audio has established itself as the 
                leading provider of premium home cinema solutions in Tirunelveli and across South India.
              </p>
              <p className="text-gray-500 mb-8 leading-relaxed">
                We specialize in end-to-end home theater services - from initial design consultation 
                and acoustic planning to professional installation, calibration, and ongoing support.
              </p>
              <Link to="/about">
                <Button variant="outline" className="border-2 border-green-600 text-green-700 hover:bg-green-600/10 transition-all duration-300">
                  Learn More About Us
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[iconMap.Award, iconMap.Users, iconMap.Shield, iconMap.Heart].map((Icon, index) => {
                const items = [
                  { title: "Certified", desc: "Professionals" },
                  { title: "15+ Years", desc: "Experience" },
                  { title: "Quality", desc: "Assured" },
                  { title: "Customer", desc: "Focused" }
                ];
                return (
                  <Card key={index} className="bg-gray-50 border-green-100 hover:border-green-500 transition-all duration-300 group">
                    <CardContent className="p-6 text-center">
                      <div className="w-12 h-12 bg-green-600/20 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:bg-green-600/30 transition-colors duration-300">
                        <Icon className="w-6 h-6 text-green-600" />
                      </div>
                      <h3 className="text-gray-900 font-semibold mb-1">{items[index].title}</h3>
                      <p className="text-gray-500 text-sm">{items[index].desc}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-green-600/20 text-green-700 border-green-600/50">Our Solutions</Badge>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Comprehensive Home Theater Services
            </h2>
            <p className="text-gray-500 text-lg max-w-3xl mx-auto">
              From concept to completion, we deliver premium home cinema solutions 
              tailored to your space, preferences, and budget.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={service.id} className="bg-white border-gray-200 hover:border-green-500 transition-all duration-300 group hover:shadow-lg hover:shadow-green-600/10">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-green-600/20 rounded-xl flex items-center justify-center mb-6 group-hover:bg-green-600/30 transition-colors duration-300">
                      <IconComponent className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-green-600 transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-gray-500 mb-6 leading-relaxed">{service.description}</p>
                    <div className="flex flex-wrap gap-2 mb-6">
                      {service.features.slice(0, 3).map((feature, index) => (
                        <Badge key={index} variant="outline" className="text-xs border-green-600/30 text-green-700">
                          {feature}
                        </Badge>
                      ))}
                    </div>
                    <Link to={`/solutions#${service.slug}`}>
                      <Button variant="ghost" className="text-green-600 hover:text-green-700 p-0 h-auto font-semibold">
                        Learn More <ChevronRight className="ml-1 w-4 h-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="text-center mt-12">
            <Link to="/solutions">
              <Button variant="outline" size="lg" className="border-2 border-green-600 text-green-700 hover:bg-green-600/10 transition-all duration-300">
                View All Solutions <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-green-600/20 text-green-700 border-green-600/50">Why Choose MJ Audio</Badge>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">Excellence in Every Project</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <Card key={index} className="bg-gray-50 border-gray-200 hover:border-green-500 transition-all duration-300 group">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-green-600/30 transition-colors duration-300">
                    <span className="text-3xl font-bold text-green-600">{item.icon}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{item.title}</h3>
                  <p className="text-gray-500 leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <BrandsCarousel />

      {/* Testimonials */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-green-600/20 text-green-700 border-green-600/50">Client Reviews</Badge>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">Trusted by hundreds of satisfied customers across South India</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="bg-white border-gray-200 hover:border-green-500 transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex items-center space-x-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-green-500 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-600 mb-6 leading-relaxed italic">"{testimonial.text}"</p>
                  <div className="flex items-center space-x-4 pt-6 border-t border-gray-100">
                    <img src={testimonial.image} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover" />
                    <div>
                      <p className="text-gray-900 font-semibold">{testimonial.name}</p>
                      <p className="text-gray-400 text-sm">{testimonial.role}</p>
                      <p className="text-green-600 text-xs mt-1">{testimonial.project}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-green-600">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">Ready to Create Your Dream Cinema?</h2>
          <p className="text-green-100 text-lg mb-8 max-w-2xl mx-auto">
            Schedule a free consultation with our experts and create a personalized home cinema solution.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-green-700 hover:bg-gray-100 px-10 py-6 text-lg transition-all duration-300 transform hover:scale-105">
                Schedule Consultation <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href={`tel:${businessInfo.phone}`}>
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-10 py-6 text-lg transition-all duration-300">
                <Phone className="mr-2 w-5 h-5" /> Call Now
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
